import { CheckCircleOutline } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Button from "@mui/material/Button";
import React, { useContext, useEffect, useMemo, useState } from "react";
import TagManager from "react-gtm-module";
import MapContext from "../../Contexts/MapContext";
import UserContext from "../../Contexts/UserContext";
import { getIsPdp, getPrimaryButtonColors } from "../../Utils/pdpUtils";
import { windowOpen } from "../../Utils/Utils";

const sourceLogos = (source) => {
  return `https://dathic-growth-files-public2.s3.us-east-1.amazonaws.com/store-locator-online-option-images/${(typeof source ===
  "string"
    ? source
    : ""
  ).replace?.(/[" "]/g, "")}.png`;
};

function BuyOnlineCard({
  source,
  additionalSource,
  isMobile,
  isPdp,
  deliveryInventory,
  filteredProducts,
  deliveryProducts,
  config,
}) {
  function getBuyUrl(additionalSource, source) {
    return (
      deliveryInventory.find(
        (inv) =>
          inv.source === source &&
          (filteredProducts.length ? filteredProducts : deliveryProducts)
            .map((p) => p.uid)
            .includes(inv.delivery_product_uid),
      )?.buy_product_store_url ||
      (filteredProducts.length ? filteredProducts : deliveryProducts).find(
        (prod) => prod.source === source,
      )?.buy_url_product ||
      deliveryInventory.find(
        (inv) =>
          inv.source === source &&
          (filteredProducts.length ? filteredProducts : deliveryProducts)
            .map((p) => p.uid)
            .includes(inv.delivery_product_uid),
      )?.delivery_entity?.store_url ||
      additionalSource?.buyUrl
    );
  }

  const renderForMobile = () => {
    return (
      <Card
        key={source}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 120,
          minWidth: 120,
          height: 135,
          alignItems: "center",
          padding: "0.5rem",
          justifyContent: "space-between",
        }}
      >
        {(additionalSource?.imgUrl || sourceLogos(source)) && (
          <CardMedia
            component="img"
            sx={{ width: "100%", objectFit: "contain", height: "50%" }}
            image={additionalSource?.imgUrl || sourceLogos(source)}
            alt={`${source} logo`}
          />
        )}

        <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
          <CheckCircleOutline style={{ color: "green" }} fontSize="small" />
          <Typography color={"green"} variant="caption">
            In Stock
          </Typography>
        </div>

        {getBuyUrl(additionalSource, source) && (
          <Button
            className="buy-source-btn"
            id={`${source}`}
            variant="contained"
            color="primary"
            fullWidth
            {...(config?.mobile?.buyButtonProps || {})}
            onClick={() => {
              const buyUrl = getBuyUrl(additionalSource, source);
              windowOpen(buyUrl, "_blank");

              TagManager.dataLayer({
                dataLayer: {
                  event: "click",
                  customClickUrl: buyUrl,
                  gtm: {
                    elementId: source,
                    elementClass: "buy-source-btn",
                    elementText: "Buy Now",
                  },
                },
              });
            }}
          >
            Buy Now
          </Button>
        )}
      </Card>
    );
  };

  const renderForPdp = () => {
    return (
      <Card
        key={source}
        elevation={1}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: 125,
          minWidth: 125,
          alignItems: "center",
          padding: "1rem",
          gap: "0.5rem",
          borderRadius: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: 48,
            borderRadius: 2,
            backgroundColor: "#f1f1f3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {(additionalSource?.imgUrl || sourceLogos(source)) && (
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 2,
              }}
              image={additionalSource?.imgUrl || sourceLogos(source)}
              alt={`${source} logo`}
            />
          )}
        </Box>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "0.875rem",
          }}
        >
          <CheckCircleOutline style={{ color: "#1f8a3b" }} fontSize="small" />
          <Typography style={{ color: "#1f8a3b" }}>In Stock</Typography>
        </div>

        {getBuyUrl(additionalSource, source) && (
          <Button
            className="buy-source-btn"
            id={`${source}`}
            variant="contained"
            color="primary"
            fullWidth
            sx={(theme) => {
              const colors = getPrimaryButtonColors(theme);
              return {
                ...colors,
                borderRadius: 999,
                textTransform: "none",
                boxShadow: "none",
                fontWeight: 600,
                "&:hover": colors,
              };
            }}
            {...(config?.mobile?.buyButtonProps || {})}
            onClick={() => {
              const buyUrl = getBuyUrl(additionalSource, source);
              windowOpen(buyUrl, "_blank");

              TagManager.dataLayer({
                dataLayer: {
                  event: "click",
                  customClickUrl: buyUrl,
                  gtm: {
                    elementId: source,
                    elementClass: "buy-source-btn",
                    elementText: "Buy Now",
                  },
                },
              });
            }}
          >
            Buy Now
          </Button>
        )}
      </Card>
    );
  };

  const renderForDesktop = () => {
    return (
      <Grid item xs={6} key={source}>
        <Card
          sx={{
            display: "flex",
            height: 135,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "66.66%",
            }}
          >
            <CardContent sx={{ flex: "1 0 auto" }}>
              <div>
                <Typography
                  style={{
                    border: "1px solid",
                    borderRadius: 5,
                    padding: "1px  6px",
                  }}
                  color={"green"}
                >
                  Available
                </Typography>
              </div>
              <Typography
                component="div"
                variant="h6"
                style={{
                  lineHeight: "1.5em",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {source}
              </Typography>
            </CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                pl: 1,
                pb: 1,
              }}
            >
              {getBuyUrl(additionalSource, source) && (
                <Button
                  className="buy-source-btn"
                  id={`${source}`}
                  variant="contained"
                  color="primary"
                  style={{ marginRight: 10 }}
                  {...(config?.desktop?.buyButtonProps || {})}
                  onClick={() => {
                    const buyUrl = getBuyUrl(additionalSource, source);
                    windowOpen(buyUrl, "_blank");

                    TagManager.dataLayer({
                      dataLayer: {
                        event: "click",
                        customClickUrl: buyUrl,
                        gtm: {
                          elementId: source,
                          elementClass: "buy-source-btn",
                          elementText: "Buy Now",
                        },
                      },
                    });
                  }}
                >
                  Buy
                </Button>
              )}
            </Box>
          </Box>
          {(additionalSource?.imgUrl || sourceLogos(source)) && (
            <div
              style={{
                width: "33.33%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "8px",
              }}
            >
              <CardMedia
                component="img"
                sx={{ width: "100%", objectFit: "contain" }}
                image={additionalSource?.imgUrl || sourceLogos(source)}
                alt={`${source} logo`}
              />
            </div>
          )}
        </Card>
      </Grid>
    );
  };
  return (
    <>
      {isPdp
        ? renderForPdp()
        : isMobile
        ? renderForMobile()
        : renderForDesktop()}
    </>
  );
}
export default function BuyOnlineButton({ products }) {
  const mapContext = React.useContext(MapContext);
  const userContext = useContext(UserContext);
  const theme = useTheme();

  const isPdp = getIsPdp();
  const isMobile = useMediaQuery("(max-width: 768px)") && !isPdp;

  const [filteredProducts, setFilteredProducts] = useState([]);

  const searchFilters = useMemo(() => mapContext.searchFilters, [
    mapContext.searchFilters,
  ]);
  const deliveryInventory = useMemo(() => mapContext.deliveryInventory || [], [
    mapContext.deliveryInventory,
  ]);
  const visibleStatesOnMap = useMemo(
    () => mapContext.visibleStatesOnMap || [],
    [mapContext.visibleStatesOnMap],
  );

  const deliveryProducts = useMemo(
    () =>
      deliveryInventory
        ?.reduce(
          (acc, inv) =>
            acc.find(
              (item) => item.delivery_product_uid === inv.delivery_product_uid,
            )
              ? acc
              : [...acc, inv],
          [],
        )
        .map((inv) => inv.delivery_product)
        .filter((product) => !!product),
    [deliveryInventory],
  );

  useEffect(() => {
    if (searchFilters?.product) {
      setFilteredProducts(
        deliveryProducts?.filter((dp) =>
          products
            ?.find((product) => `${product.id}` === `${searchFilters.product}`)
            ?.delivery_products?.map((pdp) => pdp.delivery_product_uid)
            ?.includes(dp.uid),
        ) || [],
      );
    } else {
      setFilteredProducts([]);
    }
  }, [deliveryProducts, products, searchFilters]);

  let deliveryEntities = [];
  if (userContext.state?.config?.additionalSources?.length) {
    deliveryEntities = userContext.state?.config?.additionalSources
      ?.filter(
        (s) =>
          s.deliveryArea !== "Some states" ||
          visibleStatesOnMap.some(
            (state) =>
              s.deliveryStates?.includes(state) ||
              s.deliveryStates
                ?.map((s) => (typeof s === "string" ? s : s?.key))
                .includes(state),
          ),
      )
      ?.map((s) => ({
        ...s,
        isAdditionalSource: true,
      }));
  }
  deliveryEntities = deliveryEntities?.concat(
    deliveryInventory
      .reduce(
        (acc, inv) =>
          (filteredProducts.length &&
            !filteredProducts.find(
              (prod) => prod.uid === inv.delivery_product_uid,
            )) ||
          acc.find(
            (item) => item.delivery_entity_uid === inv.delivery_entity_uid,
          )
            ? acc
            : [...acc, inv],
        [],
      )
      .map((inv) => inv.delivery_entity)
      .filter((entity) => !!entity),
  );
  return (
    <>
      {!isMobile && !isPdp && (
        <>
          <Typography
            sx={{
              color: theme.palette.primary.contrastText,
            }}
          >
            {userContext.state.config?.onlineAvailabilityMessage ||
              "Retailers available:"}
          </Typography>
        </>
      )}
      <Stack
        direction={isMobile || isPdp ? "row" : "column"}
        spacing={2}
        style={{
          flex: 1,
          height: "100%",
          overflowX: isPdp ? "auto" : undefined,
          overflowY: isPdp ? "hidden" : "auto",
          paddingBottom: isPdp ? 8 : undefined,
          alignItems: isPdp ? "flex-start" : undefined,
        }}
        padding={isMobile ? "30px" : undefined}
      >
        {[
          ...new Set(deliveryEntities?.map((entity) => entity?.source) || []),
        ].map((source) => {
          const additionalSource = deliveryEntities?.find(
            (de) => de.isAdditionalSource && de.source === source,
          );
          return !additionalSource?.isHidden ? (
            <BuyOnlineCard
              source={source}
              config={userContext.state?.config?.buyOnlineCard}
              additionalSource={additionalSource}
              isMobile={isMobile}
              isPdp={isPdp}
              deliveryInventory={deliveryInventory}
              filteredProducts={filteredProducts}
              deliveryProducts={deliveryProducts}
            />
          ) : (
            <></>
          );
        })}
      </Stack>
    </>
  );
}
